import { buriedBtnPoint } from '@op/operationLog/buriedPoint';
import React, { useEffect, useMemo, useState } from 'react';
import { Modal, Form, Button } from 'tea-component';
import { useIntl } from 'umi';
import { GPTType, PromptListType, PromptSimpleConfirmType } from '../../types';
import useGptHistoryPrompt from '../../hooks/useGptHistoryPrompt';
import HistoricalPrompt from '../../components/HistoricalPrompt';
import CommonForm from '../../PromptModal/PromptForm/CommonForm';
import './index.scss';
import { getDefaultPromptText } from '../../utils';
import AnalysisTarget from '../../PromptModal/PromptForm/AnalysisTarget';

export default function PromptModalSimple({
  handleConfirm,
  handleCancel,
  defaultActive = 1,
  promptList = [],
  wholeGameInfo,
  historyVisible,
  toggleHistoricalPrompt,
  defaultPromptText,
  isPreview = false,
  gptType = 'gpt',
  isAdvanced = false,
}: {
  handleConfirm: PromptSimpleConfirmType;
  handleCancel: () => void;
  defaultActive?: number;
  promptList: PromptListType;
  wholeGameInfo: any;
  historyVisible: boolean;
  toggleHistoricalPrompt: () => void;
  defaultPromptText?: string;
  isPreview?: boolean;
  gptType?: GPTType;
  isAdvanced?: boolean;
}) {
  const [realPromptList, promptIsArray] = useMemo(() => {
    const isArray = Array.isArray(promptList);
    if (isArray) return [promptList, isArray];

    return [promptList[gptType], isArray];
  }, [gptType]);

  function getDefaultPrompt(active: number = defaultActive) {
    if (defaultPromptText) return defaultPromptText;
    return getDefaultPromptText(realPromptList, active, gptType);
  }

  const { formatMessage } = useIntl();
  const [errorText, setErrorText] = useState('');
  const [prompt, setPrompt] = useState(getDefaultPrompt());

  useEffect(() => {
    if (isPreview) setPrompt(getDefaultPrompt());
  }, [defaultPromptText]);

  const handleGptConfirm = async () => {
    if (!isPreview) {
      setUserHistoryPrompt(prompt);
    }
    buriedBtnPoint('GPT_Confirm');
    handleConfirm?.({ prompt });
  };

  const {
    loading,
    userPromptList,
    setUserHistoryPrompt,
    getUserHistoryPrompt,
  } = useGptHistoryPrompt({
    gameInfo: wholeGameInfo,
    promptList: realPromptList,
  });

  const confirmDisabled = useMemo(() => {
    return !!errorText;
  }, [errorText]);

  if (historyVisible) {
    return (
      <HistoricalPrompt
        setPrompt={setPrompt}
        userPromptList={userPromptList}
        loading={loading}
        closeHistoricalPrompt={toggleHistoricalPrompt}
        getUserHistoryPrompt={getUserHistoryPrompt}
      ></HistoricalPrompt>
    );
  }

  return (
    <>
      <Modal.Body>
        <Form style={{ width: '100%' }} className="teaui-form-alignment">
          {isAdvanced ? (
            <AnalysisTarget
              prompt={prompt}
              setPrompt={setPrompt}
            ></AnalysisTarget>
          ) : (
            <CommonForm
              errorText={errorText}
              prompt={prompt}
              setErrorText={setErrorText}
              isPreview={isPreview}
              setPrompt={setPrompt}
              openHistorical={toggleHistoricalPrompt}
              promptList={realPromptList}
              defaultActive={defaultActive}
            ></CommonForm>
          )}
        </Form>
      </Modal.Body>
      <Modal.Footer>
        {!isPreview && (
          <Button
            type="primary"
            onClick={() => void handleGptConfirm()}
            disabled={confirmDisabled}
          >
            {formatMessage({ id: 'Confirm' })}
          </Button>
        )}
        <Button type="weak" onClick={handleCancel}>
          {formatMessage({ id: 'Close' })}
        </Button>
      </Modal.Footer>
    </>
  );
}
